import { useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { REQUEST_POST_DELETION, UNDO_POST_DELETION } from '../../graphql/mutations';
import { showInkToast } from '../cms/InkToast';
import type { PostAction } from './PostPanel';
import { KEY_HELD_TOAST } from './voiceWords';
import {
  APPROVE_SOCIAL_POST,
  Channel,
  channelName,
  dayTime,
  DELETE_SOCIAL_POST,
  DISCARD_POST_DRAFT,
  isPersonalProfile,
  postTime,
  REACH_CHANNELS,
  ReachPost,
  RETRY_SOCIAL_POST,
  SCHEDULE_POST_DRAFT,
  SKIP_SOCIAL_POST,
  UNDO_DELETE_SOCIAL_POST,
} from './reach';
import { askInk } from '../cms/InkConfirm';

/**
 * What the Reach side panel's buttons do, shared by the calendar and the
 * campaign board (UX optimization review, 2026-10-24: both open existing posts
 * in the panel). onDone runs after each success: close the panel, refetch.
 */
export function usePostActions({ onDone }: { onDone: () => unknown }) {
  const { accountId } = useAccount();
  const apollo = useApolloClient();
  const { data: channelData } = useQuery<{ socialProfiles: Channel[] }>(REACH_CHANNELS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-first',
  });
  const [approve] = useMutation(APPROVE_SOCIAL_POST);
  const [skip] = useMutation(SKIP_SOCIAL_POST);
  const [retry] = useMutation(RETRY_SOCIAL_POST);
  const [scheduleDraft] = useMutation(SCHEDULE_POST_DRAFT);
  const [discardDraft] = useMutation(DISCARD_POST_DRAFT);
  const [busy, setBusy] = useState<string | null>(null);

  const act = async (post: ReachPost, kind: PostAction, at?: Date, reason?: string) => {
    if (kind === 'post_now' || kind === 'schedule') {
      const channel = channelName(post.platform);
      // A LinkedIn company Page posts as the company, not a person (its author URN).
      const personal = isPersonalProfile(
        post.platform,
        channelData?.socialProfiles.find((c) => c.id === post.socialProfileId)?.profileId
      );
      const whenWords =
        kind === 'post_now'
          ? 'now'
          : `at ${at?.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}`;
      if (
        (kind === 'post_now' || personal) &&
        !(await askInk({
          title: `Post this to ${channel} ${whenWords}?`,
          note: personal
            ? 'This is a personal profile: it goes out under that person’s own name.'
            : undefined,
          confirmLabel: kind === 'post_now' ? 'Post now' : 'Schedule',
        }))
      )
        return;
    }
    if (
      kind === 'discard' &&
      !(await askInk({
        title: 'Delete this draft?',
        body: 'It was never posted.',
        confirmLabel: 'Delete draft',
        danger: true,
      }))
    )
      return;
    setBusy(post.id);
    try {
      if (kind === 'post_now' || kind === 'schedule') {
        const { data } = await scheduleDraft({
          variables: {
            id: post.id,
            scheduleTime: kind === 'post_now' ? 'now' : at!.toISOString(),
          },
        });
        showInkToast(
          data?.scheduleSocialPostDraft?.itemCheck?.holds
            ? KEY_HELD_TOAST
            : kind === 'post_now'
              ? 'Posting it in a minute.'
              : `Scheduled for ${dayTime(at!)}.`
        );
      } else if (kind === 'discard') {
        await discardDraft({ variables: { id: post.id } });
        showInkToast('Draft deleted.');
      } else if (kind === 'retry') {
        await retry({ variables: { id: post.id } });
        showInkToast('Trying again. It goes out in a minute.');
      } else if (kind === 'approve') {
        await approve({ variables: { id: post.id, reason } });
        const t = postTime(post);
        showInkToast(
          t && t.getTime() > Date.now()
            ? `Approved. It goes out ${dayTime(t)}.`
            : 'Approved. It goes out in a minute.'
        );
      } else {
        await skip({ variables: { id: post.id } });
        showInkToast('Skipped. It won’t go out.');
      }
      await onDone();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    } finally {
      setBusy(null);
    }
  };

  /**
   * Delete a post, with Undo on the toast: one that hasn't gone out, or one
   * that's live on its channel (the engine's next sweep takes it down).
   */
  const remove = async (post: ReachPost) => {
    const live = post.reachStatus === 'posted';
    setBusy(post.id);
    try {
      await apollo.mutate({
        mutation: live ? REQUEST_POST_DELETION : DELETE_SOCIAL_POST,
        variables: { id: post.id },
      });
      const channel = channelName(post.platform);
      const words = live
        ? `Deleting from ${channel}. It’s gone in a couple of minutes.`
        : 'Post deleted. It won’t go out.';
      showInkToast(words, {
        label: 'Undo',
        onClick: async () => {
          try {
            await apollo.mutate({
              mutation: live ? UNDO_POST_DELETION : UNDO_DELETE_SOCIAL_POST,
              variables: { id: post.id },
            });
            showInkToast(live ? `Kept on ${channel}.` : 'Post restored.');
          } catch (err) {
            showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
          }
          await onDone();
        },
      });
      await onDone();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    } finally {
      setBusy(null);
    }
  };

  return { busy, act, remove };
}
